import React from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import { useLanguage } from '../../../context/LanguageContext';
import {
    Users,
    BookOpen,
    CheckSquare,
    FileText,
    TrendingUp,
    MoreHorizontal,
    Calendar,
    Filter,
    Download,
    ChevronDown,
    Clock,
    Award,
    Zap
} from 'lucide-react';

export default function Dashboard() {
    const { t } = useLanguage();
    const stats = [
        { label: t('students'), value: '180', trend: '+12 this month', icon: Users, color: 'text-orange-500 bg-orange-50' },
        { label: t('myClasses'), value: '06', trend: 'Math & Physics', icon: BookOpen, color: 'text-red-500 bg-red-50' },
        { label: t('attendance'), value: '94%', trend: 'Class 10A leading', icon: CheckSquare, color: 'text-blue-500 bg-blue-50' },
        { label: 'To-do Grading', value: '18', trend: '3 urgent tasks', icon: FileText, color: 'text-blue-500 bg-blue-50' },
    ];

    const submissions = [
        { id: 1, student: 'Michael Scott', subject: 'Calculus Quiz', date: 'Today, 10:30 AM', status: 'Pending' },
        { id: 2, student: 'Jim Halpert', subject: 'Lab Report 04', date: 'Yesterday', status: 'Graded' },
        { id: 3, student: 'Pam Beesly', subject: 'Algebraic Equations', date: 'Mar 05, 2026', status: 'Grading' },
        { id: 4, student: 'Dwight Schrute', subject: 'Field Research', date: 'Mar 04, 2026', status: 'Graded' },
    ];

    const upcomingExams = [
        { id: 1, title: 'Final Term Math', grade: 'Grade 10A', time: 'Mar 15, 09:00 AM', room: 'Hall A' },
        { id: 2, title: 'Physics Practical', grade: 'Grade 12B', time: 'Mar 16, 11:30 AM', room: 'Lab 02' },
        { id: 3, title: 'Algebra Slip Test', grade: 'Grade 10C', time: 'Mar 18, 01:00 PM', room: 'Room 302' },
        { id: 4, title: 'Mock SAT Math', grade: 'Inter - II', time: 'Mar 20, 10:00 AM', room: 'Hall B' },
    ];

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <Sidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title={t('teacherDashboard')}
                    subtitle={t('teacherDashboardSub')}
                >
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[14px] font-black text-gray-700 shadow-sm cursor-pointer hover:bg-gray-50 transition-all group">
                        <Calendar size={18} className="text-blue-500 group-hover:scale-110 transition-transform" />
                        <span>Academic Calendar</span>
                    </div>
                </Header>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                    {stats.map((stat, idx) => (
                        <div key={idx} className="bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm hover:scale-[1.02] transition-all cursor-default group">
                            <div className="flex items-center justify-between mb-4">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${stat.color} group-hover:shadow-lg transition-all`}>
                                    <stat.icon size={22} strokeWidth={2.5} />
                                </div>
                                <div className="text-right">
                                    <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest leading-none">{stat.label}</span>
                                    <h4 className="text-[28px] font-black text-[#111827] mt-1">{stat.value}</h4>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 text-[12px] font-bold text-[#111827] opacity-60">
                                <Zap size={14} className="text-amber-500 fill-amber-500" />
                                {stat.trend}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Middle Section: Charts & Today's Summary */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                    {/* Performance Analytics (Line Chart Mock) */}
                    <div className="lg:col-span-2 bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm flex flex-col min-h-[400px]">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h3 className="text-[20px] font-black text-[#111827]">{t('classPerformanceTrends')}</h3>
                                <p className="text-gray-400 text-[14px] font-medium">Average grade percentage across current semester</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-500 rounded-lg text-[13px] font-black">
                                    <span>Grade 10A</span>
                                    <ChevronDown size={14} />
                                </div>
                                <button className="p-2 text-gray-400"><MoreHorizontal size={20} /></button>
                            </div>
                        </div>

                        <div className="flex-1 relative mt-4 px-2">
                            <svg viewBox="0 0 100 40" className="w-full h-full" preserveAspectRatio="none">
                                <defs>
                                    <linearGradient id="grad1" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" style={{ stopColor: '#3B82F6', stopOpacity: 0.2 }} />
                                        <stop offset="100%" style={{ stopColor: '#3B82F6', stopOpacity: 0 }} />
                                    </linearGradient>
                                </defs>
                                <path d="M0 35 Q 20 25, 40 30 T 80 10 T 100 5 L 100 40 L 0 40 Z" fill="url(#grad1)" />
                                <path d="M0 35 Q 20 25, 40 30 T 80 10 T 100 5" fill="none" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round" />
                                <circle cx="80" cy="11" r="3" fill="#FF4F27" stroke="white" strokeWidth="2" />
                            </svg>

                            <div className="absolute top-0 right-[15%] bg-white border border-gray-100 px-3 py-2 rounded-xl shadow-xl">
                                <p className="text-[10px] font-black text-gray-400 mb-0.5 uppercase tracking-widest">Mid Term Peak</p>
                                <p className="text-[14px] font-black text-[#111827]">88.5% Avg</p>
                            </div>
                        </div>

                        <div className="flex items-center justify-between px-2 mt-6">
                            {['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6'].map(w => (
                                <span key={w} className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{w}</span>
                            ))}
                        </div>
                    </div>

                    {/* Attendance Donut Mock */}
                    <div className="bg-blue-500 text-white p-8 rounded-[32px] shadow-2xl shadow-blue-200 flex flex-col items-center justify-center relative overflow-hidden group">
                        <div className="absolute top-[-20px] right-[-20px] w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                        <div className="relative z-10 text-center mb-8">
                            <h3 className="text-[18px] font-black mb-1">{t('classAttendance')}</h3>
                            <p className="text-white/60 text-[13px] font-bold uppercase tracking-widest leading-none">Global Summary</p>
                        </div>

                        <div className="relative w-48 h-48 z-10">
                            <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                                <circle cx="50" cy="50" r="42" fill="transparent" stroke="rgba(255,255,255,0.1)" strokeWidth="10" />
                                <circle cx="50" cy="50" r="42" fill="transparent" stroke="white" strokeWidth="10" strokeDasharray="210 264" strokeLinecap="round" />
                                <circle cx="50" cy="50" r="42" fill="transparent" stroke="#FF4F27" strokeWidth="10" strokeDasharray="40 264" strokeDashoffset="-215" strokeLinecap="round" />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-[32px] font-black leading-none">94.2%</span>
                                <span className="text-[11px] font-bold opacity-60 uppercase tracking-tighter">Engagement</span>
                            </div>
                        </div>

                        <div className="w-full mt-10 grid grid-cols-2 gap-4 relative z-10">
                            <div className="bg-white/10 p-4 rounded-2xl border border-white/10">
                                <p className="text-[20px] font-black mb-0.5">172</p>
                                <p className="text-[11px] font-bold opacity-60 uppercase">{t('present')}</p>
                            </div>
                            <div className="bg-rose-500/20 p-4 rounded-2xl border border-rose-500/20">
                                <p className="text-[20px] font-black text-rose-300 mb-0.5">08</p>
                                <p className="text-[11px] font-bold opacity-60 uppercase">{t('absent')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Section: Tables */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-10 pb-10">
                    {/* Recent Submissions */}
                    <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                        <div className="p-8 flex items-center justify-between border-b border-gray-50 bg-[#F8FAFC]/50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm">
                                    <FileText size={18} className="text-blue-500" />
                                </div>
                                <h3 className="text-[20px] font-black text-[#111827]">{t('recentStudentTask')}</h3>
                            </div>
                            <button className="text-[13px] font-bold text-blue-500 hover:underline">{t('viewAllSubmissions')}</button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-[#F8FAFC]">
                                    <tr>
                                        <th className="px-8 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase">Student Name</th>
                                        <th className="px-6 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase text-center">Assigned Subject</th>
                                        <th className="px-8 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase text-right">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {submissions.map((v, i) => (
                                        <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-blue-50/10 transition-colors group">
                                            <td className="px-8 py-5">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-black text-[11px] text-slate-400 group-hover:bg-blue-500 group-hover:text-white transition-all">{v.student.split(' ')[0][0]}</div>
                                                    <span className="text-[15px] font-black text-[#111827]">{v.student}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-5">
                                                <p className="text-[14px] font-bold text-gray-800 text-center">{v.subject}</p>
                                                <p className="text-[11px] font-bold text-gray-400 text-center">{v.date}</p>
                                            </td>
                                            <td className="px-8 py-5 text-right">
                                                <span className={`text-[11px] font-black px-3 py-1.5 rounded-lg uppercase tracking-widest ${v.status === 'Pending' ? 'text-amber-500 bg-amber-50/50' :
                                                    v.status === 'Grading' ? 'text-blue-500 bg-blue-50/50' :
                                                        'text-emerald-500 bg-emerald-50/50'
                                                    }`}>
                                                    {v.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Upcoming Exams Table */}
                    <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                        <div className="p-8 flex items-center justify-between border-b border-gray-50 bg-[#F8FAFC]/50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm">
                                    <Award size={18} className="text-rose-500" />
                                </div>
                                <h3 className="text-[20px] font-black text-[#111827]">{t('upcomingSchedule')}</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 bg-rose-500 rounded-full animate-pulse"></span>
                                <span className="text-[12px] font-bold text-gray-400">Next exam in 8 days</span>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-[#F8FAFC]">
                                    <tr>
                                        <th className="px-8 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase">Exam Title</th>
                                        <th className="px-6 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase text-center">Class/Room</th>
                                        <th className="px-8 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase text-right">Timing</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {upcomingExams.map((exam, i) => (
                                        <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-rose-50/10 transition-colors group">
                                            <td className="px-8 py-5 text-[15px] font-black text-[#111827]">{exam.title}</td>
                                            <td className="px-6 py-5 text-center">
                                                <p className="text-[14px] font-black text-gray-800">{exam.grade}</p>
                                                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{exam.room}</p>
                                            </td>
                                            <td className="px-8 py-5 text-right">
                                                <div className="flex items-center justify-end gap-2 text-[14px] font-black text-blue-500">
                                                    <Clock size={14} />
                                                    {exam.time.split(',')[1]}
                                                </div>
                                                <p className="text-[11px] font-bold text-gray-400">{exam.time.split(',')[0]}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
